import type { Editor } from '@tiptap/core';

import { t } from '../i18n';
import { Glyph } from './icons';

/**
 * The inline marks shared by the selection bubble and the mobile insert bar.
 */
export function EditorFormatControls({
  editor,
  keepEditorFocus = false,
}: {
  editor: Editor;
  keepEditorFocus?: boolean;
}): React.ReactElement {
  const keepFocus = keepEditorFocus
    ? (event: React.PointerEvent): void => event.preventDefault()
    : undefined;
  return (
    <>
      <button
        type="button"
        onPointerDown={keepFocus}
        className={editor.isActive('bold') ? 'selbar__btn nb is-on' : 'selbar__btn nb'}
        aria-label={t('editor.bold')}
        aria-pressed={editor.isActive('bold')}
        title={t('editor.bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Glyph d={['M7 5h6a3.5 3.5 0 0 1 0 7H7z', 'M7 12h7a3.5 3.5 0 0 1 0 7H7z']} size={16} />
      </button>
      <button
        type="button"
        onPointerDown={keepFocus}
        className={editor.isActive('italic') ? 'selbar__btn nb is-on' : 'selbar__btn nb'}
        aria-label={t('editor.italic')}
        aria-pressed={editor.isActive('italic')}
        title={t('editor.italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Glyph d={['M19 4h-9', 'M14 20H5', 'M15 4L9 20']} size={16} />
      </button>
      <button
        type="button"
        onPointerDown={keepFocus}
        className={editor.isActive('underline') ? 'selbar__btn nb is-on' : 'selbar__btn nb'}
        aria-label={t('editor.underline')}
        aria-pressed={editor.isActive('underline')}
        title={t('editor.underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Glyph d={['M6 4v6a6 6 0 0 0 12 0V4', 'M4 20h16']} size={16} />
      </button>
      <button
        type="button"
        onPointerDown={keepFocus}
        className={editor.isActive('strike') ? 'selbar__btn nb is-on' : 'selbar__btn nb'}
        aria-label={t('editor.strikethrough')}
        aria-pressed={editor.isActive('strike')}
        title={t('editor.strikethrough')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Glyph
          d={[
            'M16 6.5C15.3 5.3 13.9 4.5 12 4.5c-2.5 0-4 1.2-4 3 0 3.8 8 2.2 8 6.5 0 1.8-1.7 3-4 3-2 0-3.6-.9-4.3-2.3',
            'M4 12h16',
          ]}
          size={16}
        />
      </button>
      <button
        type="button"
        onPointerDown={keepFocus}
        className={editor.isActive('code') ? 'selbar__btn nb is-on' : 'selbar__btn nb'}
        aria-label={t('editor.code')}
        aria-pressed={editor.isActive('code')}
        title={t('editor.code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Glyph d={['M16 18l6-6-6-6', 'M8 6l-6 6 6 6']} size={16} />
      </button>
    </>
  );
}
